import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './InstructorDashboard.css';
import './ReportDashboard.css';
import './InstructorStatistics.css';
import Sidebar from './Sidebar';
import Header from './Header';
import { apiFetch } from './api';
import HomePage from './HomePage';
import InstructorExams from './InstructorExams';
import InstructorCourses from './InstructorCourses';
import InstructorStudents from './InstructorStudents';
import InstructorStatistics from './InstructorStatistics';
import CourseQuestions from './CourseQuestions';
import StudyTopics from './StudyTopics';
import Assignments from './Assignments';

const EXAM_TYPE_LABELS = {
  'odev': 'Ödev', 'Odev': 'Ödev', 'Ödev': 'Ödev',
  'final': 'Dönem Sonu Sınavı', 'Final': 'Dönem Sonu Sınavı',
  'quiz': 'Kısa Sınav', 'Quiz': 'Kısa Sınav',
  'vize': 'Ara Sınav', 'Vize': 'Ara Sınav', 'Ara Sınav': 'Ara Sınav',
  'butunleme': 'Bütünleme', 'bütünleme': 'Bütünleme', 'Butunleme': 'Bütünleme', 'Bütünleme': 'Bütünleme',
  'Ontest': 'Ön Test', 'ontest': 'Ön Test',
};
const getExamTypeLabel = (type) => EXAM_TYPE_LABELS[type] || type || '';

function RectorDashboard({ user, userType = 'rektor', onLogout, isImpersonating = false, onReturnToAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('home');
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
  const lastPathnameRef = useRef(location.pathname);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const getPageFromUrl = (pathname) => {
    if (pathname.startsWith('/rector/')) {
      const page = pathname.replace('/rector/', '');
      if (page === '' || page === 'home') return 'home';
      return page;
    }
    return 'home';
  };

  useEffect(() => {
    if (location.pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = location.pathname;
      setActivePage(getPageFromUrl(location.pathname));
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePageChange = (page) => {
    setActivePage(page);
    setSelectedExam(null);
    setSelectedCourse(null);
    const pagePath = `/rector/${page}`;
    lastPathnameRef.current = pagePath;
    navigate(pagePath);
    if (isMobile) setIsSidebarOpen(false);
  };

  return (
    <div className="instructor-dashboard">
      <Header
        user={user}
        onLogout={onLogout}
        onProfileClick={() => {}}
        onHomeClick={() => handlePageChange('home')}
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        title="KOSTÜ Sınav Modülü"
      />

      <Sidebar
        userType={userType}
        activePage={activePage}
        onPageChange={handlePageChange}
        user={user}
        isImpersonating={isImpersonating}
        onReturnToAdmin={onReturnToAdmin}
        isOpen={isMobile ? isSidebarOpen : true}
        onClose={isMobile ? () => setIsSidebarOpen(false) : undefined}
      />

      <div className="dashboard-main">
        <div className={`dashboard-content ${activePage === 'home' && !selectedExam && !selectedCourse ? 'full-height' : ''}`}>
          {selectedExam ? (
            <div className="dashboard-container">
              <ExamStudentsPage exam={selectedExam} onBack={() => setSelectedExam(null)} />
            </div>
          ) : selectedCourse ? (
            <div className="dashboard-container">
              <CourseStatsPage course={selectedCourse} onBack={() => setSelectedCourse(null)} onExamClick={setSelectedExam} />
            </div>
          ) : activePage === 'home' ? (
            <HomePage userType={userType} onPageChange={handlePageChange} />
          ) : (
            <div className="dashboard-container">
              {activePage === 'students' && <InstructorStudents instructorId={user?.id} />}
              {activePage === 'courses' && <InstructorCourses instructorId={user?.id} />}
              {activePage === 'statistics' && <InstructorStatistics instructorId={user?.id} />}
              {activePage === 'sinav-raporu' && <RectorSinavRaporu onCourseClick={setSelectedCourse} />}
              {activePage === 'akademisyen-sinav-raporu' && <AkademisyenSinavRaporu onCourseClick={setSelectedCourse} />}
              {activePage === 'exams' && <InstructorExams instructorId={user?.id} isAdmin={false} isProgramBaskan={false} />}
              {activePage === 'sorular' && <CourseQuestions userType="rektor" />}
              {activePage === 'calisma-konulari' && <StudyTopics userType="rektor" />}
              {activePage === 'odevler' && <Assignments userType={userType} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RectorSinavRaporu({ onCourseClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFaculties, setExpandedFaculties] = useState({});
  const [expandedPrograms, setExpandedPrograms] = useState({});

  useEffect(() => {
    apiFetch('/rector/dashboard')
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          setData(res);
        } else {
          setError(res.message || 'Veri alınamadı');
        }
      })
      .catch(() => setError('Sunucu hatası'))
      .finally(() => setLoading(false));
  }, []);

  const toggleFaculty = (id) => setExpandedFaculties((p) => ({ ...p, [id]: !p[id] }));
  const toggleProgram = (id) => setExpandedPrograms((p) => ({ ...p, [id]: !p[id] }));

  if (loading) return <div className="report-loading">Yükleniyor...</div>;
  if (error) return <div className="report-error">{error}</div>;

  const faculties = data?.faculties || [];
  const totalExams = faculties.reduce((s, f) => s + (f.exam_count || 0), 0);
  const totalPrograms = faculties.reduce((s, f) => s + (f.program_count || 0), 0);

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <h2 className="report-title">Üniversite Geneli Sınav Raporu</h2>
          <p className="report-subtitle">Tüm fakülteler ve bölümler</p>
        </div>
        <div className="report-stats-row">
          <div className="report-stat-box">
            <span className="report-stat-number">{faculties.length}</span>
            <span className="report-stat-label">Fakülte</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">{totalPrograms}</span>
            <span className="report-stat-label">Bölüm</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">{totalExams}</span>
            <span className="report-stat-label">Toplam Sınav</span>
          </div>
        </div>
      </div>

      {faculties.length === 0 ? (
        <div className="report-empty">Fakülte bulunamadı.</div>
      ) : (
        faculties.map((faculty) => (
          <div key={faculty.faculty_id} className="report-faculty-block">
            <button
              className="report-faculty-header"
              onClick={() => toggleFaculty(faculty.faculty_id)}
            >
              <div className="report-faculty-name">{faculty.faculty_name}</div>
              <div className="report-course-meta">
                <span className="report-badge-program">{faculty.program_count} Bölüm</span>
                <span className="report-badge-exam">{faculty.exam_count} Sınav</span>
                <span className={`report-chevron ${expandedFaculties[faculty.faculty_id] ? 'open' : ''}`}>▼</span>
              </div>
            </button>

            {expandedFaculties[faculty.faculty_id] && (
              <div className="report-faculty-body">
                {(faculty.programs || []).length === 0 ? (
                  <div className="report-empty-sub">Bu fakültede bölüm bulunamadı.</div>
                ) : (
                  (faculty.programs || []).map((program) => (
                    <div key={program.program_id} className="report-program-block">
                      <button
                        className="report-program-header"
                        onClick={() => toggleProgram(program.program_id)}
                      >
                        <div className="report-program-name">{program.program_name}</div>
                        <div className="report-course-meta">
                          <span className="report-badge-course">{program.course_count} Ders</span>
                          <span className="report-badge-exam">{program.exam_count} Sınav</span>
                          <span className={`report-chevron ${expandedPrograms[program.program_id] ? 'open' : ''}`}>▼</span>
                        </div>
                      </button>

                      {expandedPrograms[program.program_id] && (
                        <div className="report-program-body">
                          {(program.courses || []).length === 0 ? (
                            <div className="report-empty-sub">Bu bölümde ders bulunamadı.</div>
                          ) : (
                            (program.courses || []).map((course) => (
                              <div key={course.course_id} className="report-course-block">
                                <button
                                  className="report-course-header"
                                  onClick={() => onCourseClick(course)}
                                >
                                  <div className="report-course-name">
                                    <span className="report-course-code">{course.course_code}</span>
                                    {course.course_name}
                                  </div>
                                  <div className="report-course-meta">
                                    <span className="report-badge-exam">{course.exam_count} Sınav</span>
                                    <span className="report-chevron">▶</span>
                                  </div>
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function CourseStatsPage({ course, onBack, onExamClick }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(`/instructor/courses/${course.course_id}/statistics`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setStats(res);
        else setError(res.message || 'İstatistikler alınamadı.');
      })
      .catch(() => setError('Sunucu hatası.'))
      .finally(() => setLoading(false));
  }, [course.course_id]);

  const courseLabel = course.course_code
    ? `${course.course_code} — ${course.course_name}`
    : course.course_name;

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <button onClick={onBack} className="exam-students-back-btn">← Geri</button>
          <h2 className="report-title" style={{ marginTop: '0.5rem' }}>{courseLabel}</h2>
        </div>
      </div>

      {loading && <div className="report-loading">Yükleniyor...</div>}
      {error && <div className="report-error">{error}</div>}

      {!loading && !error && stats && (
        <>
          <div className="statistics-grid">
            <div className="stat-card">
              <div className="stat-content">
                <h3>Tamamlanmış Sınav</h3>
                <p className="stat-value">{stats.course_completed_exams || 0}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-content">
                <h3>Toplam Öğrenci</h3>
                <p className="stat-value">{stats.course_students || 0}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-content">
                <h3>Bekleyen Sınavlar</h3>
                <p className="stat-value">{stats.course_pending_exams || 0}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-content">
                <h3>Sınıf ortalaması</h3>
                <p className="stat-value" style={{ color: '#2e7d32' }}>
                  {typeof stats.department_average === 'number'
                    ? stats.department_average.toFixed(2)
                    : (stats.class_average ?? 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {stats.exam_weights && stats.exam_weights.length > 0 && (
            <div className="exam-weights-table-wrap">
              <h3 className="course-stats-subtitle">Sınav ağırlıkları ve tür ortalamaları</h3>
              <table className="course-stats-table">
                <thead>
                  <tr>
                    <th>Sınav türü</th>
                    <th>Ağırlık (%)</th>
                    <th>Not ortalaması</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.exam_type_averages && stats.exam_type_averages.length > 0
                    ? stats.exam_type_averages.map((eta) => (
                        <tr key={eta.exam_weight_id}>
                          <td>{getExamTypeLabel(eta.exam_type)}</td>
                          <td>{stats.exam_weights.find((ew) => ew.id === eta.exam_weight_id)?.weight ?? '-'}</td>
                          <td>{eta.average.toFixed(2)}</td>
                        </tr>
                      ))
                    : stats.exam_weights.map((ew) => (
                        <tr key={ew.id}>
                          <td>{getExamTypeLabel(ew.exam_type)}</td>
                          <td>{ew.weight}</td>
                          <td>—</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {course.exams && course.exams.length > 0 && (
        <div className="report-exam-table-wrapper">
          <table className="report-exam-table">
            <thead>
              <tr>
                <th>Sınav Adı</th>
                <th>Mod</th>
                <th>Tip</th>
                <th>Durum</th>
                <th>Başlangıç</th>
                <th>Bitiş</th>
                <th>Giren Öğrenci</th>
              </tr>
            </thead>
            <tbody>
              {course.exams.map((exam) => (
                <tr key={exam.id} onClick={() => onExamClick(exam)} style={{ cursor: 'pointer' }}>
                  <td className="report-exam-title">{exam.title}</td>
                  <td>{exam.mode === 'Online' ? 'Online' : 'Fiziksel'}</td>
                  <td>{examTypeLabel(exam.type)}</td>
                  <td><span className={`report-status-badge status-${(exam.status || '').toLowerCase()}`}>{examStatusLabel(exam.status)}</span></td>
                  <td>{formatDate(exam.start_at)}</td>
                  <td>{formatDate(exam.end_at)}</td>
                  <td className="report-attempt-count">{exam.attempt_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {course.exams && course.exams.length === 0 && (
        <div className="report-empty">Bu ders için henüz sınav yapılmamış.</div>
      )}
    </div>
  );
}

function ExamStudentsPage({ exam, onBack }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isFiziksel = exam.mode !== 'Online';

  useEffect(() => {
    if (isFiziksel) { setLoading(false); return; }
    apiFetch(`/rector/exams/${exam.id}/students`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setStudents(res.students || []);
        else setError(res.message || 'Öğrenci listesi alınamadı.');
      })
      .catch(() => setError('Sunucu hatası.'))
      .finally(() => setLoading(false));
  }, [exam.id, isFiziksel]);

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <button onClick={onBack} className="exam-students-back-btn">← Geri</button>
          <h2 className="report-title" style={{ marginTop: '0.5rem' }}>{exam.title}</h2>
          <p className="report-subtitle">
            {exam.mode === 'Online' ? 'Online' : 'Fiziksel'} · {examTypeLabel(exam.type)} ·{' '}
            <span className={`report-status-badge status-${(exam.status || '').toLowerCase()}`}>
              {examStatusLabel(exam.status)}
            </span>
          </p>
        </div>
        {!loading && !error && !isFiziksel && (
          <div className="report-stats-row">
            <div className="report-stat-box">
              <span className="report-stat-number">{students.length}</span>
              <span className="report-stat-label">Toplam Öğrenci</span>
            </div>
            <div className="report-stat-box">
              <span className="report-stat-number">{students.filter(s => s.status === 'Submitted').length}</span>
              <span className="report-stat-label">Sınava Giren</span>
            </div>
            <div className="report-stat-box">
              <span className="report-stat-number">{students.filter(s => s.status !== 'Submitted').length}</span>
              <span className="report-stat-label">Girmeyen</span>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="report-loading">Yükleniyor...</div>}
      {error && <div className="report-error">{error}</div>}
      {!loading && !error && isFiziksel && (
        <div className="report-empty">Bu fiziksel sınava ait online sonuç bulunmamaktadır.</div>
      )}
      {!loading && !error && !isFiziksel && students.length === 0 && (
        <div className="report-empty">Bu derse kayıtlı öğrenci bulunamadı.</div>
      )}
      {!loading && !error && !isFiziksel && students.length > 0 && (
        <div className="report-exam-table-wrapper">
          <table className="report-exam-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Öğrenci No</th>
                <th>Öğrenci Adı</th>
                <th>Durum</th>
                <th>Puan</th>
                <th>Başlangıç</th>
                <th>Bitiş</th>
                <th>İhlal</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => {
                const girdi = s.status === 'Submitted';
                return (
                  <tr key={s.attempt_id || s.student_number} style={{ opacity: girdi ? 1 : 0.6 }}>
                    <td style={{ color: '#9ca3af', textAlign: 'center' }}>{i + 1}</td>
                    <td>{s.student_number}</td>
                    <td style={{ fontWeight: 500 }}>{s.student_name}</td>
                    <td>
                      <span className={`report-status-badge ${girdi ? 'status-completed' : 'status-draft'}`}>
                        {girdi ? 'Teslim Etti' : 'Girmedi'}
                      </span>
                    </td>
                    <td className="report-attempt-count" style={{ color: girdi ? '#4CAF50' : '#9ca3af' }}>
                      {girdi ? s.total_points : '-'}
                    </td>
                    <td>{formatDate(s.started_at)}</td>
                    <td>{formatDate(s.submitted_at)}</td>
                    <td style={{ textAlign: 'center', color: s.violation_count > 0 ? '#dc2626' : '#9ca3af' }}>
                      {girdi ? s.violation_count : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AkademisyenSinavRaporu({ onCourseClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAkademisyenler, setExpandedAkademisyenler] = useState({});
  const [siralamaGorunum, setSiralamaGorunum] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    apiFetch('/rector/akademisyen-dashboard')
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setData(res);
        else setError(res.message || 'Veri alınamadı');
      })
      .catch(() => setError('Sunucu hatası'))
      .finally(() => setLoading(false));
  }, []);

  const toggleAkademisyen = (id) => setExpandedAkademisyenler((p) => ({ ...p, [id]: !p[id] }));

  if (loading) return <div className="report-loading">Yükleniyor...</div>;
  if (error) return <div className="report-error">{error}</div>;

  const akademisyenler = data?.akademisyenler || [];
  const totalExams = akademisyenler.reduce((s, a) => s + (a.exam_count || 0), 0);
  const siraliTam = [...akademisyenler].sort((a, b) => b.exam_count - a.exam_count);
  const filtered = siraliTam.filter((a) =>
    a.akademisyen_adi.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const rozetRenk = (rank) => {
    if (rank === 0) return { bg: '#fef9c3', border: '#fbbf24', text: '#92400e', medal: '🥇' };
    if (rank === 1) return { bg: '#f1f5f9', border: '#94a3b8', text: '#475569', medal: '🥈' };
    if (rank === 2) return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', medal: '🥉' };
    return { bg: 'white', border: '#e2e8f0', text: '#1e293b', medal: null };
  };

  return (
    <div className="report-wrapper">
      <div className="report-header-card">
        <div className="report-header-info">
          <h2 className="report-title">Akademisyen Bazında Sınav Raporu</h2>
          <p className="report-subtitle">Tüm akademisyenler ve sınavları</p>
        </div>
        <div className="report-stats-row">
          <div className="report-stat-box">
            <span className="report-stat-number">{akademisyenler.length}</span>
            <span className="report-stat-label">Akademisyen</span>
          </div>
          <div className="report-stat-box">
            <span className="report-stat-number">{totalExams}</span>
            <span className="report-stat-label">Toplam Sınav</span>
          </div>
        </div>
      </div>

      <div className="akademisyen-search-row">
        <input
          className="akademisyen-search-input"
          type="text"
          placeholder="Akademisyen adıyla ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Sıralama / Detay sekme geçişi */}
      <div className="akademisyen-tab-row">
        <button
          className={`akademisyen-tab-btn ${siralamaGorunum ? 'active' : ''}`}
          onClick={() => setSiralamaGorunum(true)}
        >
          En Aktif Sıralama
        </button>
        <button
          className={`akademisyen-tab-btn ${!siralamaGorunum ? 'active' : ''}`}
          onClick={() => setSiralamaGorunum(false)}
        >
          Ders &amp; Sınav Detayı
        </button>
      </div>

      {siralamaGorunum ? (
        /* ── Sıralama tablosu ── */
        akademisyenler.length === 0 ? (
          <div className="report-empty">Henüz sınav oluşturmuş akademisyen bulunamadı.</div>
        ) : filtered.length === 0 ? (
          <div className="report-empty">"{searchQuery}" için sonuç bulunamadı.</div>
        ) : (
          <div className="report-exam-table-wrapper" style={{ marginTop: 0 }}>
            <table className="report-exam-table">
              <thead>
                <tr>
                  <th style={{ width: 48, textAlign: 'center' }}>Sıra</th>
                  <th>Akademisyen</th>
                  <th>Fakülte / Birim</th>
                  <th style={{ textAlign: 'center' }}>Ders Sayısı</th>
                  <th style={{ textAlign: 'center' }}>Sınav Sayısı</th>
                  <th style={{ width: 140 }}>Aktivite</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const realRank = siraliTam.findIndex((x) => x.akademisyen_id === a.akademisyen_id);
                  const { bg, border, text, medal } = rozetRenk(realRank);
                  const maxExam = siraliTam[0]?.exam_count || 1;
                  const barPct = Math.round((a.exam_count / maxExam) * 100);
                  return (
                    <tr key={a.akademisyen_id} style={{ background: bg }}>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: text, fontSize: '1rem' }}>
                        {medal ? medal : <span style={{ color: '#9ca3af' }}>{realRank + 1}</span>}
                      </td>
                      <td style={{ fontWeight: realRank < 3 ? 700 : 500, color: text }}>
                        {a.akademisyen_adi}
                      </td>
                      <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                        {a.faculty || '—'}
                      </td>
                      <td style={{ textAlign: 'center', color: '#4a5568' }}>
                        {a.course_count}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className="report-badge-exam"
                          style={{
                            background: `rgba(76,175,80,${0.08 + 0.22 * (a.exam_count / maxExam)})`,
                            border: `1px solid ${border}`,
                            color: text,
                            fontSize: '0.82rem',
                            padding: '0.2rem 0.65rem',
                          }}
                        >
                          {a.exam_count}
                        </span>
                      </td>
                      <td>
                        <div className="akademisyen-bar-track">
                          <div
                            className="akademisyen-bar-fill"
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        /* ── Detay accordion ── */
        akademisyenler.length === 0 ? (
          <div className="report-empty">Henüz sınav oluşturmuş akademisyen bulunamadı.</div>
        ) : filtered.length === 0 ? (
          <div className="report-empty">"{searchQuery}" için sonuç bulunamadı.</div>
        ) : (
          filtered.map((akademisyen) => (
            <div key={akademisyen.akademisyen_id} className="report-faculty-block">
              <button
                className="report-faculty-header"
                onClick={() => toggleAkademisyen(akademisyen.akademisyen_id)}
              >
                <div className="report-faculty-name">
                  {akademisyen.akademisyen_adi}
                  {akademisyen.faculty && (
                    <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '0.75rem', opacity: 0.75 }}>
                      {akademisyen.faculty}
                    </span>
                  )}
                </div>
                <div className="report-course-meta">
                  <span className="report-badge-course">{akademisyen.course_count} Ders</span>
                  <span className="report-badge-exam">{akademisyen.exam_count} Sınav</span>
                  <span className={`report-chevron ${expandedAkademisyenler[akademisyen.akademisyen_id] ? 'open' : ''}`}>▼</span>
                </div>
              </button>

              {expandedAkademisyenler[akademisyen.akademisyen_id] && (
                <div className="report-faculty-body">
                  {(akademisyen.courses || []).length === 0 ? (
                    <div className="report-empty-sub">Bu akademisyene ait ders bulunamadı.</div>
                  ) : (
                    (akademisyen.courses || []).map((course) => (
                      <div key={course.course_id} className="report-course-block">
                        <button
                          className="report-course-header"
                          onClick={() => onCourseClick(course)}
                        >
                          <div className="report-course-name">
                            <span className="report-course-code">{course.course_code}</span>
                            {course.course_name}
                          </div>
                          <div className="report-course-meta">
                            <span className="report-badge-exam">{course.exam_count} Sınav</span>
                            <span className="report-chevron">▶</span>
                          </div>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )
      )}
    </div>
  );
}

function examTypeLabel(type) {
  if (type === 'Test') return 'Test';
  if (type === 'Classic') return 'Klasik';
  if (type === 'Mixed') return 'Karışık';
  return type || '-';
}

function examStatusLabel(status) {
  if (status === 'Draft') return 'Taslak';
  if (status === 'Published') return 'Yayında';
  if (status === 'Paused') return 'Duraklatıldı';
  if (status === 'Completed') return 'Tamamlandı';
  if (status === 'Archived') return 'Arşivlendi';
  return status || '-';
}

function formatDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

export { RectorSinavRaporu, AkademisyenSinavRaporu, ExamStudentsPage, CourseStatsPage };
export default RectorDashboard;
